import React, { useState, useEffect } from "react";
import axios from "axios";
import { useBusinessPlan } from "../App";
import { useAuth } from "../contexts/AuthContext";
import { Button } from "./ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "./ui/card";
import { Input } from "./ui/input";
import { Label } from "./ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "./ui/select";
import { Building2, Plus, TrendingUp, DollarSign } from "lucide-react";
import { toast } from "sonner";
import { CURRENCIES } from "../constants/currencies";
import { useUserEntitlements } from "../hooks/useUserEntitlements";
import { LimitIndicator, LimitWarning } from "../components/governance";

const BusinessPlanList = () => {
  const { createNewPlan } = useBusinessPlan();
  const { user } = useAuth();
  const { canCreate, getLimit, entitlements, loading: entitlementsLoading } = useUserEntitlements();
  const [showCreateForm, setShowCreateForm] = useState(false);
  const [formData, setFormData] = useState({
    name: "",
    company_name: "",
    currency: "USD",
    fiscal_year_start: 1,
    subsidiary_id: ""
  });
  const [subsidiaries, setSubsidiaries] = useState([]);
  const [loading, setLoading] = useState(false);

  // Fetch subsidiaries and auto-fill company name
  useEffect(() => {
    fetchSubsidiaries();
    // Auto-fill company name from user profile if available
    if (user?.company_name && !formData.company_name) {
      setFormData(prev => ({
        ...prev,
        company_name: user.company_name
      }));
    }
  }, [user]);

  const fetchSubsidiaries = async () => {
    try {
      // Use company_only=true for optimized query
      const response = await axios.get(`${process.env.REACT_APP_BACKEND_URL}/api/enterprise/subsidiaries?company_only=true`);
      setSubsidiaries(response.data || []);
    } catch (error) {
      console.error("Error fetching subsidiaries:", error);
      setSubsidiaries([]);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!formData.name.trim() || !formData.company_name.trim()) {
      toast.error("Please fill in all required fields");
      return;
    }

    // For enterprise users, require subsidiary selection
    if (subsidiaries.length > 0 && !formData.subsidiary_id) {
      toast.error("Please select a subsidiary for this business plan");
      return;
    }

    setLoading(true);
    try {
      await createNewPlan(formData);
      toast.success("Business plan created successfully!");
      setShowCreateForm(false);
    } catch (error) {
      console.error('❌ Error creating business plan:', error);
      
      // Check if 403 - limit exceeded
      if (error.response && error.response.status === 403) {
        const userTier = entitlements?.tier?.toLowerCase();
        
        // Show CTA for Starter tier
        if (userTier === 'starter') {
          toast.error(
            <div className="space-y-2">
              <p className="font-bold">Business Plan Limit Reached</p>
              <p className="text-sm">Upgrade to Professional or buy an add-on to create more business plans.</p>
              <div className="flex gap-2 mt-2">
                <button 
                  onClick={() => window.location.href = '/pricing'}
                  className="text-xs bg-blue-600 text-white px-3 py-1 rounded hover:bg-blue-700"
                >
                  Upgrade Now
                </button>
                <button 
                  onClick={() => window.location.href = '/add-ons'}
                  className="text-xs bg-green-600 text-white px-3 py-1 rounded hover:bg-green-700"
                >
                  Buy Add-on
                </button>
              </div>
            </div>,
            { duration: 10000 }
          );
        } else {
          // For other tiers, generic limit message
          toast.error("You have reached your business plan limit. Please upgrade your subscription.");
        }
      } else {
        toast.error("Failed to create business plan: " + (error.response?.data?.detail?.message || error.message));
      }
    } finally {
      setLoading(false);
    }
  };

  const handleInputChange = (field, value) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const months = [
    { value: 1, label: "January" },
    { value: 2, label: "February" },
    { value: 3, label: "March" },
    { value: 4, label: "April" },
    { value: 5, label: "May" },
    { value: 6, label: "June" },
    { value: 7, label: "July" },
    { value: 8, label: "August" },
    { value: 9, label: "September" },
    { value: 10, label: "October" },
    { value: 11, label: "November" },
    { value: 12, label: "December" }
  ];

  if (showCreateForm) {
    return (
      <div className="min-h-screen flex items-center justify-center p-6">
        <div className="w-full max-w-2xl">
          <div className="text-center mb-8">
            <div className="flex items-center justify-center mb-4">
              <div className="bg-gradient-to-r from-blue-600 to-purple-600 rounded-2xl p-4">
                <Building2 className="h-8 w-8 text-white" />
              </div>
            </div>
            <h1 className="text-4xl font-bold gradient-text mb-2">
              Create Your Business Plan
            </h1>
            <p className="text-slate-600 text-lg">
              Start building your comprehensive financial model
            </p>
          </div>

          <Card className="glass-card shadow-strong">
            <CardHeader>
              <CardTitle className="text-2xl">Business Plan Setup</CardTitle>
              <CardDescription>
                Configure your business plan with basic information and preferences
              </CardDescription>
            </CardHeader>
            <CardContent>
              <form onSubmit={handleSubmit} className="space-y-6">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div className="space-y-2">
                    <Label htmlFor="plan-name">Plan Name *</Label>
                    <Input
                      id="plan-name"
                      placeholder="e.g., 2025 Strategic Plan"
                      value={formData.name}
                      onChange={(e) => handleInputChange('name', e.target.value)}
                      className="form-input"
                    />
                  </div>
                  
                  <div className="space-y-2">
                    <Label htmlFor="company-name">
                      Company Name {formData.company_name ? '(Optional)' : '*'}
                    </Label>
                    <Input
                      id="company-name"
                      placeholder={formData.company_name || "e.g., Tech Solutions Inc."}
                      value={formData.company_name}
                      onChange={(e) => handleInputChange('company_name', e.target.value)}
                      className="form-input"
                    />
                    {user?.company_name && formData.company_name === user.company_name && (
                      <p className="text-xs text-gray-500">Auto-filled from your profile. You can change it if needed.</p>
                    )}
                  </div>
                </div>

                {/* Enterprise Subsidiary Selection */}
                {subsidiaries.length > 0 && (
                  <div className="space-y-2">
                    <Label htmlFor="subsidiary">Subsidiary/Company *</Label>
                    <Select value={formData.subsidiary_id} onValueChange={(value) => handleInputChange('subsidiary_id', value)}>
                      <SelectTrigger className="form-select">
                        <SelectValue placeholder="Select subsidiary for this business plan" />
                      </SelectTrigger>
                      <SelectContent>
                        {subsidiaries.map((subsidiary) => (
                          <SelectItem key={subsidiary.id} value={subsidiary.id}>
                            {subsidiary.name} ({subsidiary.code})
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                    <p className="text-sm text-slate-600">
                      This business plan will be created for the selected subsidiary
                    </p>
                  </div>
                )}

                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div className="space-y-2">
                    <Label htmlFor="currency">Currency</Label>
                    <Select value={formData.currency} onValueChange={(value) => handleInputChange('currency', value)}>
                      <SelectTrigger className="form-select">
                        <SelectValue placeholder="Select currency" />
                      </SelectTrigger>
                      <SelectContent>
                        {CURRENCIES.map((currency) => (
                          <SelectItem key={currency.value} value={currency.value}>
                            {currency.label}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="fiscal-year">Fiscal Year Start</Label>
                    <Select 
                      value={formData.fiscal_year_start.toString()} 
                      onValueChange={(value) => handleInputChange('fiscal_year_start', parseInt(value))}
                    >
                      <SelectTrigger className="form-select">
                        <SelectValue placeholder="Select month" />
                      </SelectTrigger>
                      <SelectContent>
                        {months.map((month) => (
                          <SelectItem key={month.value} value={month.value.toString()}>
                            {month.label}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                </div>

                <div className="flex items-center justify-between pt-6">
                  <Button
                    type="button"
                    variant="outline"
                    onClick={() => setShowCreateForm(false)}
                    className="btn-secondary"
                  >
                    Back
                  </Button>
                  <Button
                    type="submit"
                    disabled={loading}
                    className="btn-primary"
                  >
                    {loading ? (
                      <>
                        <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                        Creating...
                      </>
                    ) : (
                      <>
                        <Plus className="h-4 w-4 mr-2" />
                        Create Business Plan
                      </>
                    )}
                  </Button>
                </div>
              </form>
            </CardContent>
          </Card>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen flex items-center justify-center p-6">
      <div className="w-full max-w-4xl text-center">
        {/* Hero Section */}
        <div className="mb-12">
          <div className="flex items-center justify-center mb-6">
            <img 
              src="/TavPlans_Logo.png" 
              alt="TavPlans Logo" 
              className="h-24 w-24 object-contain"
            />
          </div>
          <h1 className="text-6xl font-bold gradient-text mb-4">
            TavPlans
          </h1>
          <p className="text-xl text-slate-600 mb-2">
            Business Planning & Financial Modeling
          </p>
          <p className="text-slate-500 max-w-2xl mx-auto">
            Create comprehensive business plans with advanced financial modeling, 
            scenario analysis, and strategic planning tools.
          </p>
        </div>

        {/* Features Grid */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-12">
          <Card className="glass-card card-hover">
            <CardContent className="p-6 text-center">
              <div className="bg-blue-100 rounded-2xl p-4 w-16 h-16 mx-auto mb-4 flex items-center justify-center">
                <TrendingUp className="h-8 w-8 text-blue-600" />
              </div>
              <h3 className="text-lg font-semibold mb-2">Financial Modeling</h3>
              <p className="text-slate-600 text-sm">
                Build comprehensive P&L, Balance Sheet, and Cash Flow statements with real-time calculations
              </p>
            </CardContent>
          </Card>

          <Card className="glass-card card-hover">
            <CardContent className="p-6 text-center">
              <div className="bg-purple-100 rounded-2xl p-4 w-16 h-16 mx-auto mb-4 flex items-center justify-center">
                <DollarSign className="h-8 w-8 text-purple-600" />
              </div>
              <h3 className="text-lg font-semibold mb-2">Scenario Planning</h3>
              <p className="text-slate-600 text-sm">
                Create multiple scenarios (Base, Best, Worst) with dynamic multipliers and sensitivity analysis
              </p>
            </CardContent>
          </Card>

          <Card className="glass-card card-hover">
            <CardContent className="p-6 text-center">
              <div className="bg-green-100 rounded-2xl p-4 w-16 h-16 mx-auto mb-4 flex items-center justify-center">
                <Building2 className="h-8 w-8 text-green-600" />
              </div>
              <h3 className="text-lg font-semibold mb-2">Strategic Planning</h3>
              <p className="text-slate-600 text-sm">
                Track OKRs, KPIs, and strategic objectives across multiple business pillars
              </p>
            </CardContent>
          </Card>
        </div>

        {/* Limit Warning */}
        <div className="mb-6">
          <LimitWarning limitType="business_plans" />
        </div>

        {/* CTA */}
        <div>
          <Button
            onClick={() => {
              setShowCreateForm(true);
            }}
            size="lg"
            className="btn-primary text-lg px-8 py-4 mb-4"
          >
            <Plus className="h-5 w-5 mr-2" />
            Create Your First Business Plan
          </Button>
          
          {/* Limit Indicator */}
          <div className="max-w-md mx-auto mb-4">
            <LimitIndicator limitType="business_plans" compact />
          </div>
          
          <p className="text-sm text-slate-500">
            Start building comprehensive financial models for your business
          </p>
        </div>

        {/* Hero Image */}
        <div className="mt-12">
          <div className="relative rounded-2xl overflow-hidden shadow-strong">
            <img
              src="https://images.unsplash.com/photo-1720700126957-769e2f2fc0fc"
              alt="Business Planning Team"
              className="w-full h-64 object-cover"
            />
            <div className="absolute inset-0 bg-gradient-to-t from-black/50 to-transparent"></div>
            <div className="absolute bottom-4 left-4 text-white">
              <p className="text-sm font-medium">Professional Financial Planning</p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default BusinessPlanList;